import { useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Global storage for scroll positions
// Using a simple object that persists as long as the app is running
const scrollPositions: Record<string, number> = {};

/**
 * Hook to restore and save scroll position of a container
 * @param containerRef React ref to the scrollable element
 * @param key Optional override for the scroll key. Defaults to location.key or location.pathname
 */
export function useScrollRestoration(containerRef: React.RefObject<HTMLElement>, key?: string) {
    const location = useLocation();

    // location.key is unique for each history entry (useful for back/forward)
    // fallback to pathname for general persistence
    const scrollKey = key || location.key || location.pathname;

    useLayoutEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        // Restore position
        const savedPosition = scrollPositions[scrollKey];
        if (savedPosition !== undefined) {
            container.scrollTop = savedPosition;
        }

        const handleScroll = () => {
            if (container) {
                scrollPositions[scrollKey] = container.scrollTop;
            }
        };

        container.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            container.removeEventListener('scroll', handleScroll);
        };
    }, [scrollKey]);
}
